/**
 * A GLOBÁLIS CÉGVÁLASZTÓ (#73) — `c`, vagy a palettából.
 *
 * SZŰRŐ, NEM NAVIGÁCIÓ (`design-brief.md` §3): a választás mindhárom
 * felületre érvényes, és a felület ugyanaz marad, csak más cégre szűkítve.
 *
 * A parancspaletta mintája: combobox + listbox, a fókusz végig a mezőn, a
 * nyilak mozgatnak, `Enter` választ, `Escape` zár. Natív `<select>` nincs —
 * a D9 tilalma, mert elnyeli a billentyűket.
 *
 * Gépelésre szűr: a pontos Tensoft-kód elöl (`PI` + `Enter`), utána név és
 * adószám (`rankCompanies`). Üres mezőnél „Az összes cég" és a legutóbbiak.
 */

import { useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialogFocus } from '@/hooks/useDialogFocus';
import { getSelectedCompany, recentCompanies, setSelectedCompany } from '@/lib/company-selection';
import { type PickerCompany, rankCompanies } from './rankCompanies';
import type { CompanyPickerState } from './useCompanyPicker';

/** „Az összes cég" sora — nem cég, hanem a szűrő kikapcsolása. */
const ALL = 'all';

interface Option {
  id: string;
  company: PickerCompany | null;
}

/** A választó ablaka, ha nyitva van — a váz így nem ágazik el miatta. */
export function CompanyPicker({ picker }: { picker: CompanyPickerState }) {
  return picker.isOpen ? <PickerDialog picker={picker} /> : null;
}

function PickerDialog({ picker }: { picker: CompanyPickerState }) {
  const { t } = useTranslation();
  const { companies, close } = picker;
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const dialog = useDialogFocus<HTMLDivElement>(input);

  const options = useMemo<Option[]>(() => {
    if (companies.status !== 'ready') {
      return [];
    }
    const ranked = rankCompanies(companies.companies, query, recentCompanies()).map((company) => ({
      id: company.id,
      company,
    }));
    // Gépelés közben „az összes cég" nem találat — csak üres mezőnél áll elöl.
    return query.trim() === '' ? [{ id: ALL, company: null }, ...ranked] : ranked;
  }, [companies, query]);

  // A KIJELÖLÉS KÖVETI A SZŰKÍTÉST — mint a palettán: ha kiesik, az elsőre lép.
  const selected = options.find((option) => option.id === selectedId)?.id ?? options[0]?.id ?? null;

  function choose(id: string) {
    close();
    setSelectedCompany(id === ALL ? null : id);
  }

  function step(delta: number) {
    if (options.length === 0) {
      return;
    }
    const at = options.findIndex((option) => option.id === selected);
    setSelectedId(options[(at + delta + options.length) % options.length].id);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    // A választó billentyűi nem szivárognak ki a `window`-on ülő regiszterbe.
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        step(1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        step(-1);
        return;
      case 'Enter':
        event.preventDefault();
        event.stopPropagation();
        if (selected !== null) choose(selected);
        return;
      default:
        return;
    }
  }

  return (
    <div className="overlay">
      <button
        type="button"
        className="overlay__backdrop"
        aria-label={t('companyPicker.close')}
        tabIndex={-1}
        onClick={close}
      />

      <div
        className="card palette"
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-label={t('companyPicker.title')}
      >
        <input
          ref={input}
          className="palette__query"
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={selected === null ? undefined : `${listId}-${selected}`}
          aria-label={t('companyPicker.title')}
          placeholder={t('companyPicker.placeholder')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
        />

        <PickerBody
          picker={picker}
          options={options}
          selected={selected}
          listId={listId}
          onChoose={choose}
        />
      </div>
    </div>
  );
}

/** A lista — vagy hogy miért nincs (töltődik, nem jött meg, nincs találat). */
function PickerBody({
  picker,
  options,
  selected,
  listId,
  onChoose,
}: {
  picker: CompanyPickerState;
  options: Option[];
  selected: string | null;
  listId: string;
  onChoose: (id: string) => void;
}) {
  const { t } = useTranslation();
  const current = getSelectedCompany() ?? ALL;

  if (picker.companies.status === 'loading') {
    return <p className="empty">{t('companyPicker.loading')}</p>;
  }
  if (picker.companies.status === 'failed') {
    return (
      <p className="empty" role="alert">
        {t('companyPicker.loadFailed')}
      </p>
    );
  }
  if (options.length === 0) {
    return <p className="empty">{t('companyPicker.noMatch')}</p>;
  }
  return (
    <div className="palette__list" role="listbox" id={listId} aria-label={t('companyPicker.title')}>
      {options.map((option) => (
        <div
          key={option.id}
          id={`${listId}-${option.id}`}
          role="option"
          tabIndex={-1}
          aria-selected={option.id === selected}
          className={option.id === selected ? 'palette__item palette__item--on' : 'palette__item'}
          onMouseDown={(event) => {
            // `onMouseDown`, nem `onClick` — a palettánál leírt okból: a
            // kattintás elvenné a fókuszt a mezőtől.
            event.preventDefault();
            onChoose(option.id);
          }}
        >
          <span>
            {option.company === null ? picker.allLabel : option.company.name}
            {option.id === current ? ` · ${t('companyPicker.current')}` : ''}
          </span>
          {option.company === null ? null : <CompanyMeta company={option.company} />}
        </div>
      ))}
    </div>
  );
}

function CompanyMeta({ company }: { company: PickerCompany }) {
  return (
    <span className="company-picker__meta">
      {company.tensoftCode === null ? null : <kbd>{company.tensoftCode}</kbd>}{' '}
      <span className="num">{company.taxNumber}</span>
    </span>
  );
}

/**
 * A FEJLÉC SORA: „Cég: …" — és egyben a választó gombja.
 *
 * Egyetlen cégnél (ügyfél) nincs mit választani, ott nem is jelenik meg.
 * A név a cégsorból jön; amíg az töltődik, a választás azonosító helyett
 * semleges „…" áll, nem egy villanó üres sor.
 */
export function CompanyTrigger({ picker }: { picker: CompanyPickerState }) {
  const { t } = useTranslation();
  if (!picker.canPick) {
    return null;
  }

  return (
    <p className="scope">
      {t('companyFilter.label')}:{' '}
      <button type="button" className="scope__switch" onClick={picker.open}>
        {triggerLabel(picker, getSelectedCompany(), t('companyPicker.inactive'))} <kbd>c</kbd>
      </button>
    </p>
  );
}

function triggerLabel(
  picker: CompanyPickerState,
  selected: string | null,
  inactive: string
): string {
  if (selected === null) {
    return picker.allLabel;
  }
  if (picker.companies.status !== 'ready') {
    return '…';
  }
  const company = picker.companies.companies.find((one) => one.id === selected);
  // ⚠️ NEM „az összes cég", ha nincs a listán: a választás ÉL (a szerver
  // elfogadta), csak a cég közben inaktív lett. A „minden cég" felirat azt
  // hazudná, hogy a lista nincs szűrve.
  if (!company) {
    return inactive;
  }
  return `${company.tensoftCode === null ? '' : `${company.tensoftCode} · `}${company.name}`;
}
